import {
  BOOKING_TYPE_TO_SERVICE_ID,
  PLATFORM_FIXED_PRICES,
  loadPublishedAvailability,
} from '../../referrer/utils/servicesAvailabilityStorage';
import type { Professional, WorkExperienceEntry } from '../data/bookingData';
import { SERVICE_CONFIGS } from '../data/bookingData';
import type { ServiceType } from '../data/bookingData';
import { fallbackRankScore } from '../../../shared/utils/ranking';
import {
  cleanExtractedName,
  cleanExtractedText,
  stripInvisibleCharacters,
} from '../../../shared/utils/formatters';

type CachedApiResponse<T> = {
  success: boolean;
  data?: T;
  error?: string;
};

export type MarketplaceApiClient = {
  getReferrers: () => Promise<CachedApiResponse<unknown[]>>;
  getServiceConfigurationsForService: (serviceId: string) => Promise<CachedApiResponse<unknown[]>>;
};

// Kept short because this cache drives marketplace *eligibility* (which
// services a professional appears under). A long TTL means a professional
// toggling a service off/on stays visible/hidden incorrectly in other users'
// already-open tabs for up to the TTL window.
const CACHE_TTL_MS = 20_000;

interface MarketplaceBaseData {
  referrers: any[];
  configsByService: Record<string, any[]>;
  unifiedConfigs: any[];
}

let cache: { data: MarketplaceBaseData; fetchedAt: number } | null = null;
let inflight: Promise<MarketplaceBaseData> | null = null;

function normalizeProfileSkills(skills: unknown): string[] {
  if (Array.isArray(skills)) {
    return skills.map(String).filter(Boolean);
  }
  if (typeof skills === 'string' && skills.trim()) {
    return skills.split(',').map((s) => s.trim()).filter(Boolean);
  }
  return [];
}

/** Maps stored work_history_entries into the marketplace WorkExperienceEntry shape. */
export function mapWorkHistoryToExperience(raw: unknown): WorkExperienceEntry[] {
  if (!Array.isArray(raw)) return [];
  return raw
    .map((e: any) => {
      const role = cleanExtractedText(e?.role) || '';
      const company = cleanExtractedText(e?.company) || '';
      if (!role && !company) return null;
      const start = [e?.startMonth, e?.startYear].filter(Boolean).join(' ');
      const end = e?.currentlyWorking
        ? 'Present'
        : [e?.endMonth, e?.endYear].filter(Boolean).join(' ');
      const period = start && end ? `${start} – ${end}` : start || end || '';
      return {
        role: role || company,
        company,
        period,
        summary: typeof e?.description === 'string' ? e.description : '',
      } as WorkExperienceEntry;
    })
    .filter((e): e is WorkExperienceEntry => e !== null);
}

export function buildProfessionalsForService(
  serviceType: ServiceType,
  profiles: any[],
  serviceConfigs: any[],
  unifiedConfigs: any[]
): Professional[] {
  const serviceId = BOOKING_TYPE_TO_SERVICE_ID[serviceType];
  const svcConfig = SERVICE_CONFIGS[serviceType];
  const serviceConfigByUser = new Map(serviceConfigs.map((row: any) => [row.user_id, row]));

  const mapped: Professional[] = profiles.map((profile: any) => {
    const customConfig = serviceConfigByUser.get(profile.id);
    const unifiedConfig = unifiedConfigs.find((c: any) => c.user_id === profile.id);

    // Price is whatever the professional set on their My Services page, and
    // nothing else. Resume review is the single exception: it is platform-fixed
    // at PLATFORM_FIXED_PRICES['resume-review'] and professionals cannot change
    // it, which mirrors payment_settings.default_price_inr on the server.
    //
    // Previously this substituted DEFAULT_SERVICE_PRICES (₹500 / ₹800) whenever
    // price was NULL, so an unpriced professional was listed as though they had
    // chosen that figure. The server resolves the real charge from the same
    // service_configurations row, so any invented number here is a quote the
    // checkout will not honour.
    const configuredPrice =
      customConfig && typeof customConfig.price === 'number' && customConfig.price > 0
        ? customConfig.price
        : null;
    const platformFixedPrice = serviceId ? PLATFORM_FIXED_PRICES[serviceId] ?? null : null;
    const price = configuredPrice ?? platformFixedPrice;

    let effectiveDuration: number | null = null;
    if (svcConfig.schedulingModel === 'fixed_duration_call') {
      effectiveDuration = svcConfig.slotDurationMinutes;
    } else if (svcConfig.schedulingModel === 'variable_duration_call') {
      effectiveDuration =
        customConfig?.slot_duration_minutes ??
        customConfig?.referral_call_duration ??
        (svcConfig.allowedDurations ? svcConfig.allowedDurations[0] : 20);
    }

    let availabilityMap = unifiedConfig?.availability || customConfig?.availability || {};
    if (!availabilityMap || Object.keys(availabilityMap).length === 0) {
      const localPub = loadPublishedAvailability(profile.id);
      availabilityMap = localPub['unified-availability'] || (serviceId ? localPub[serviceId] : undefined) || {};
    }
    const profileSkills = normalizeProfileSkills(profile.skills);
    const rating = typeof profile.rating === 'number' ? profile.rating : 0.0;
    const reviews = typeof profile.reviews_count === 'number' ? profile.reviews_count : 0;
    // Server-computed multi-signal rank when available (migration 026);
    // otherwise a client-side Bayesian-rating fallback.
    const rankScore =
      typeof profile.rank_score === 'number'
        ? profile.rank_score
        : fallbackRankScore(profile.rating, reviews);

    const rawName = profile.full_name || profile.name || 'Referrer';
    const cleanName = cleanExtractedName(rawName) || 'Referrer';
    const cleanTitle = cleanExtractedText(profile.designation) || 'Referrer';
    const cleanCompany = cleanExtractedText(profile.company_name) || undefined;
    const cleanBio = stripInvisibleCharacters(profile.bio) || undefined;

    return {
      id: profile.id,
      alias: cleanName,
      name: cleanName,
      title: cleanTitle,
      company: cleanCompany,
      rating,
      reviews,
      rankScore,
      // Non-null by the time a caller sees this: the filter below drops every
      // entry where price could not be resolved.
      price: price as number,
      description: cleanBio || 'Professional available for mentorship sessions.',
      specialties: profileSkills,
      skills: profileSkills,
      weeklySlots: [],
      image: profile.profile_picture || undefined,
      yearsOfExperience: profile.years_of_experience || undefined,
      verified: !!profile.is_verified,
      designation: cleanTitle || undefined,
      bio: cleanBio,
      workExperience: mapWorkHistoryToExperience(profile.work_history_entries),
      linkedinLink: profile.linkedin_url || undefined,
      githubLink: profile.github_link || undefined,
      portfolioUrl: profile.portfolio_url || undefined,
      userCode: profile.user_code || undefined,
      verifiedCompanyDomain: profile.verified_company_domain || undefined,
      location: stripInvisibleCharacters(profile.location) || undefined,
      industry: stripInvisibleCharacters(profile.industry) || undefined,
      slotDurationMinutes: effectiveDuration ?? undefined,
      availabilityMap,
      offersService: Boolean(customConfig) && price !== null,
    } as Professional;
  });

  // A professional appears under a service only when they have an active config
  // row for it AND a resolved price. `serviceConfigs` is already filtered to
  // is_active = true upstream (api.ts), so the row's presence covers the first
  // half; `price !== null` covers the second.
  //
  // The price half matters because it is the only thing keeping the listing
  // honest: the server refuses to create a payment order for a service the
  // professional has not priced (migration 057), so listing them here would
  // send buyers to a checkout that cannot open. Hiding them instead makes the
  // failure happen at listing time, where nobody has committed anything yet.
  const filtered = mapped.filter((p) => (p as any).offersService === true);

  return filtered.sort((a, b) => {
    return (
      (b.rankScore ?? 0) - (a.rankScore ?? 0) ||
      b.rating - a.rating ||
      b.reviews - a.reviews
    );
  });
}

export function getCachedProfessionalsForService(serviceType: ServiceType): Professional[] | null {
  if (!cache || Date.now() - cache.fetchedAt > CACHE_TTL_MS) {
    return null;
  }

  const serviceId = BOOKING_TYPE_TO_SERVICE_ID[serviceType];
  if (!serviceId) return null;

  return buildProfessionalsForService(
    serviceType,
    cache.data.referrers,
    cache.data.configsByService[serviceId] || [],
    cache.data.unifiedConfigs
  );
}

export async function fetchMarketplaceBaseData(client: MarketplaceApiClient): Promise<MarketplaceBaseData> {
  if (cache && Date.now() - cache.fetchedAt < CACHE_TTL_MS) {
    return cache.data;
  }

  if (inflight) {
    return inflight;
  }

  inflight = (async () => {
    const [
      referrersRes,
      resumeReviewRes,
      interviewMockRes,
      referralSessionRes,
      unifiedRes,
    ] = await Promise.all([
      client.getReferrers(),
      client.getServiceConfigurationsForService('resume-review'),
      client.getServiceConfigurationsForService('interview-mock'),
      client.getServiceConfigurationsForService('referral-session'),
      client.getServiceConfigurationsForService('unified-availability'),
    ]);

    const referrersOk = referrersRes.success && Array.isArray(referrersRes.data);
    if (!referrersOk) {
      if (cache) {
        inflight = null;
        return cache.data;
      }
      throw new Error(referrersRes.error || 'Failed to load referrers');
    }

    const data: MarketplaceBaseData = {
      referrers: referrersRes.data ?? [],
      configsByService: {
        'resume-review':
          resumeReviewRes.success && resumeReviewRes.data ? resumeReviewRes.data : cache?.data.configsByService['resume-review'] || [],
        'interview-mock':
          interviewMockRes.success && interviewMockRes.data ? interviewMockRes.data : cache?.data.configsByService['interview-mock'] || [],
        'referral-session':
          referralSessionRes.success && referralSessionRes.data ? referralSessionRes.data : cache?.data.configsByService['referral-session'] || [],
      },
      unifiedConfigs:
        unifiedRes.success && unifiedRes.data ? unifiedRes.data : cache?.data.unifiedConfigs || [],
    };

    cache = { data, fetchedAt: Date.now() };
    inflight = null;
    return data;
  })().catch((error) => {
    inflight = null;
    throw error;
  });

  return inflight;
}

export function mapCachedProfessionals(serviceType: ServiceType, base: MarketplaceBaseData): Professional[] {
  const serviceId = BOOKING_TYPE_TO_SERVICE_ID[serviceType];
  return buildProfessionalsForService(
    serviceType,
    base.referrers,
    serviceId ? base.configsByService[serviceId] || [] : [],
    base.unifiedConfigs
  );
}

export function invalidateMarketplaceCache() {
  cache = null;
  inflight = null;
}
